import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExploreReveal } from "./ExploreReveal";
import { useExploreInView } from "./useExploreInView";

const LEASE_STEPS = [
  {
    labelKey: "Explore_Lease_Step_Application",
    bodyKey: "Explore_Lease_Step_Application_Body",
  },
  {
    labelKey: "Explore_Lease_Step_Background",
    bodyKey: "Explore_Lease_Step_Background_Body",
  },
  {
    labelKey: "Explore_Lease_Step_Offer",
    bodyKey: "Explore_Lease_Step_Offer_Body",
  },
  {
    labelKey: "Explore_Lease_Step_MoveIn",
    bodyKey: "Explore_Lease_Step_MoveIn_Body",
  },
  {
    labelKey: "Explore_Lease_Step_MoveOut",
    bodyKey: "Explore_Lease_Step_MoveOut_Body",
  },
] as const;

const STEP_MS = 1700;

export function ExploreLeaseFlowSection() {
  const { t } = useTranslation();
  const { ref, isInView } = useExploreInView<HTMLDivElement>({
    threshold: 0.2,
  });
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isInView) {
      setActiveIndex(0);
      return;
    }

    setActiveIndex(0);
    const id = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % LEASE_STEPS.length);
    }, STEP_MS);

    return () => window.clearInterval(id);
  }, [isInView]);

  const progress = (activeIndex + 1) / LEASE_STEPS.length;

  return (
    <section className="explore-section explore-section--surface-white">
      <div className="explore-section-inner">
        <ExploreReveal>
          <p className="explore-kicker">{t("Explore_Lease_Kicker")}</p>
          <h2 className="explore-display-title">{t("Explore_Lease_Title")}</h2>
        </ExploreReveal>

        <div
          ref={ref}
          className={`explore-timeline${isInView ? " is-visible" : ""}`}
        >
          <div className="explore-timeline-rail" aria-hidden>
            <div className="explore-timeline-rail-base" />
            <div
              className="explore-timeline-rail-progress"
              style={{
                transform: `scaleY(${isInView ? progress : 0})`,
              }}
            />
            <div
              className="explore-timeline-rail-head"
              style={{
                top: `calc(${progress * 100}% - 0.45rem)`,
                opacity: isInView ? 1 : 0,
              }}
            />
          </div>

          <ol className="explore-timeline-steps">
            {LEASE_STEPS.map((step, index) => {
              const state =
                index < activeIndex
                  ? "is-complete"
                  : index === activeIndex
                    ? "is-active"
                    : "is-pending";

              return (
                <li
                  key={step.labelKey}
                  className={`explore-timeline-step ${state}`}
                  style={{
                    transitionDelay: isInView
                      ? `${80 + index * 90}ms`
                      : undefined,
                  }}
                >
                  <span className="explore-timeline-node" aria-hidden>
                    <span className="explore-timeline-node-ring" />
                    <span className="explore-timeline-node-core" />
                  </span>
                  <div className="explore-timeline-copy">
                    <div className="explore-timeline-meta">
                      <span className="explore-timeline-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="explore-timeline-label">
                        {t(step.labelKey)}
                      </span>
                    </div>
                    <p className="explore-timeline-body">{t(step.bodyKey)}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
